import { supabase } from '../supabase';
import SearchService from '../services/SearchService.js';

/**
 * Detect biohack mentions in text content
 * @param {string} text - The text content to search
 * @returns {Promise<Array>} Array of biohack objects that were mentioned
 */
export const detectBiohackMentions = async (text) => {
  if (!text || !text.trim()) return [];
  
  // Extract all words from the text
  const words = text.split(/\s+/).filter(word => word.length >= 2);
  const detectedBiohacks = [];
  const seenIds = new Set();
  
  // Check each word against biohacks
  for (const word of words) {
    // Remove punctuation for matching
    const cleanWord = word.replace(/[.,;:!?()[\]{}"'`]/g, '');
    if (cleanWord.length < 2) continue;
    
    try {
      const results = await SearchService.searchBiohacksByName(cleanWord, 10);
      for (const biohack of results) {
        // Check if the biohack name exactly matches the word (case-insensitive)
        if (biohack.name.toLowerCase() === cleanWord.toLowerCase() && !seenIds.has(biohack.id)) {
          detectedBiohacks.push(biohack);
          seenIds.add(biohack.id);
        }
      }
    } catch (error) {
      console.error('Error detecting biohack mentions:', error);
    }
  }
  
  return detectedBiohacks;
};

/**
 * Extract biohacks from a protocol object
 * Handles multiple protocol data formats and fetches biohacks if needed
 * @param {Object} protocol - The protocol object
 * @returns {Promise<Array>} Array of biohack objects
 */
export const getBiohacksFromProtocol = async (protocol) => {
  if (!protocol || !protocol.id) {
    return [];
  }

  try {
    // First, try to extract biohacks from the protocol object if they're already loaded
    // Handle protocol.biohacks format
    if (protocol.biohacks && Array.isArray(protocol.biohacks)) {
      return protocol.biohacks.filter(b => b && b.id);
    }

    // Handle protocol.protocol_hacks format (with nested biohacks)
    if (protocol.protocol_hacks && Array.isArray(protocol.protocol_hacks)) {
      const biohacks = protocol.protocol_hacks
        .map(ph => ph.biohacks)
        .filter(Boolean)
        .filter(b => b.id);
      if (biohacks.length > 0) {
        return biohacks;
      }
    }

    // Handle protocol.hacks format
    if (protocol.hacks && Array.isArray(protocol.hacks)) {
      return protocol.hacks.filter(b => b && b.id);
    }

    // If biohacks aren't loaded, fetch them from the database
    const { data: protocolHacks, error } = await supabase
      .from('protocol_hacks')
      .select(`
        hack_id,
        biohacks (
          id,
          name,
          image_url,
          description,
          category,
          ease_of_use_score,
          time_efficiency_score,
          affordability_score
        )
      `)
      .eq('protocol_id', protocol.id)
      .order('position', { ascending: true });

    if (error) {
      console.error('Error fetching biohacks from protocol:', error);
      return [];
    }

    if (!protocolHacks || protocolHacks.length === 0) {
      return [];
    }

    // Extract biohacks from the fetched data
    const biohacks = protocolHacks
      .map(ph => ph.biohacks)
      .filter(Boolean)
      .filter(b => b.id);

    return biohacks;
  } catch (error) {
    console.error('Error in getBiohacksFromProtocol:', error);
    return [];
  }
};

/**
 * Extract biohacks from a protocol report
 * Gets biohacks from the protocol associated with the report
 * @param {Object} protocolReport - The protocol report object (from protocol_report_snapshots)
 * @returns {Promise<Array>} Array of biohack objects
 */
export const getBiohacksFromProtocolReport = async (protocolReport) => {
  if (!protocolReport || !protocolReport.protocol_id) {
    return [];
  }

  try {
    // First, try to get biohacks from the protocol if it's already loaded in the report
    if (protocolReport.protocols && protocolReport.protocols.protocol_hacks) {
      const biohacks = protocolReport.protocols.protocol_hacks
        .map(ph => ph.biohacks)
        .filter(Boolean)
        .filter(b => b && b.id);
      if (biohacks.length > 0) {
        return biohacks;
      }
    }

    // Try to get biohacks from protocol_snapshot if available
    if (protocolReport.protocol_snapshot) {
      const protocol = protocolReport.protocol_snapshot;
      if (protocol.protocol_hacks && Array.isArray(protocol.protocol_hacks)) {
        const biohacks = protocol.protocol_hacks
          .map(ph => ph.biohacks)
          .filter(Boolean)
          .filter(b => b && b.id);
        if (biohacks.length > 0) {
          return biohacks;
        }
      }
      if (protocol.hacks && Array.isArray(protocol.hacks)) {
        return protocol.hacks.filter(b => b && b.id);
      }
    }

    // If biohacks aren't loaded, fetch them from the database using protocol_id
    const { data: protocolHacks, error } = await supabase
      .from('protocol_hacks')
      .select(`
        hack_id,
        biohacks (
          id,
          name,
          image_url,
          description,
          category,
          ease_of_use_score,
          time_efficiency_score,
          affordability_score
        )
      `)
      .eq('protocol_id', protocolReport.protocol_id)
      .order('position', { ascending: true });

    if (error) {
      console.error('Error fetching biohacks from protocol report:', error);
      return [];
    }

    if (!protocolHacks || protocolHacks.length === 0) {
      return [];
    }

    // Extract biohacks from the fetched data
    const biohacks = protocolHacks
      .map(ph => ph.biohacks)
      .filter(Boolean)
      .filter(b => b.id);

    return biohacks;
  } catch (error) {
    console.error('Error in getBiohacksFromProtocolReport:', error);
    return [];
  }
};

/**
 * Extract biomarkers from a protocol report
 * Gets biomarkers from the biomarker_data JSONB field
 * @param {Object} protocolReport - The protocol report object (from protocol_report_snapshots)
 * @returns {Promise<Array>} Array of biomarker objects with id and name
 */
export const getBiomarkersFromProtocolReport = async (protocolReport) => {
  if (!protocolReport || !protocolReport.biomarker_data) {
    return [];
  }

  try {
    // biomarker_data is a JSONB array of biomarker objects
    // Each object has: { id, name, unit, ... }
    const biomarkerData = protocolReport.biomarker_data;
    
    if (!Array.isArray(biomarkerData) || biomarkerData.length === 0) {
      return [];
    }

    // Extract unique biomarker IDs from the biomarker_data
    const biomarkerIds = Array.from(
      new Set(
        biomarkerData
          .map(bm => bm.id)
          .filter(Boolean)
      )
    );

    if (biomarkerIds.length === 0) {
      return [];
    }

    // Fetch full biomarker details from the database
    const { data: biomarkers, error } = await supabase
      .from('biomarkers')
      .select('id, name, category, unit')
      .in('id', biomarkerIds);

    if (error) {
      console.error('Error fetching biomarkers from protocol report:', error);
      // Fallback: return basic info from biomarker_data if fetch fails
      return biomarkerData
        .filter(bm => bm.id && bm.name)
        .map(bm => ({
          id: bm.id,
          name: bm.name,
          category: bm.category || null,
          unit: bm.unit || null
        }));
    }

    if (!biomarkers || biomarkers.length === 0) {
      // Fallback: return basic info from biomarker_data
      return biomarkerData
        .filter(bm => bm.id && bm.name)
        .map(bm => ({
          id: bm.id,
          name: bm.name,
          category: bm.category || null,
          unit: bm.unit || null
        }));
    }

    return biomarkers;
  } catch (error) {
    console.error('Error in getBiomarkersFromProtocolReport:', error);
    return [];
  }
};

/**
 * Create post_biohack_tags records for detected biohacks
 * @param {string} postId - The post ID
 * @param {Array} biohacks - Array of biohack objects
 */
export const createBiohackTags = async (postId, biohacks) => {
  if (!postId || !biohacks || biohacks.length === 0) return;
  
  try {
    // Remove duplicates
    const uniqueBiohacks = Array.from(
      new Map(biohacks.map(b => [b.id, b])).values()
    );
    
    const biohackRows = uniqueBiohacks.map((biohack) => ({
      post_id: postId,
      biohack_id: biohack.id
    }));
    
    const { data, error } = await supabase
      .from('post_biohack_tags')
      .insert(biohackRows)
      .select();
    
    if (error) {
      console.error('Error creating biohack tags:', error);
      // Don't throw - this is not critical for post creation
    } else {
      console.log('Successfully created biohack tags:', data);
    }
  } catch (error) {
    console.error('Error in createBiohackTags:', error);
    // Don't throw - this is not critical for post creation
  }
};

/**
 * Create post_biomarker_tags records for selected biomarkers
 * @param {string} postId - The post ID
 * @param {Array} biomarkers - Array of biomarker objects
 */
export const createBiomarkerTags = async (postId, biomarkers) => {
  if (!postId || !biomarkers || biomarkers.length === 0) return;
  
  try {
    // Remove duplicates
    const uniqueBiomarkers = Array.from(
      new Map(biomarkers.map(b => [b.id, b])).values()
    );
    
    const biomarkerRows = uniqueBiomarkers.map((biomarker) => ({
      post_id: postId,
      biomarker_id: biomarker.id
    }));
    
    const { data, error } = await supabase
      .from('post_biomarker_tags')
      .insert(biomarkerRows)
      .select();
    
    if (error) {
      console.error('Error creating biomarker tags:', error);
      // Don't throw - this is not critical for post creation
    } else {
      console.log('Successfully created biomarker tags:', data);
    }
  } catch (error) {
    console.error('Error in createBiomarkerTags:', error);
    // Don't throw - this is not critical for post creation
  }
};

export const validatePostData = (content, title) => {
  if (!title || !title.trim()) {
    throw new Error('Title is required');
  }

  if (title.length > 500) {
    throw new Error('Title must be less than 500 characters');
  }

  if (content && content.length > 50000) {
    throw new Error('Content must be less than 50,000 characters');
  }
};

export const createPost = async (postData) => {
  const { error: postError, data: postInsertData } = await supabase
    .from('posts')
    .insert(postData)
    .select('id')
    .single();

  if (postError) throw postError;
  return postInsertData?.id;
};

export const processAttachment = async (postId, attachment) => {
  try {
    // Biohacks are now handled separately via createBiohackTags
    // Skip them here to avoid duplicate tag creation
    if (attachment.type === 'biohacks') {
      return;
    }

      // Create post_attachment record for other attachment types
      const attachmentType = attachment.type === 'protocol' ? 'protocols' : 
                            attachment.type === 'protocolReport' ? 'report' : 
                            attachment.type;
    const { data: attachmentData, error: attachmentError } = await supabase
      .from('post_attachments')
      .insert({
        post_id: postId,
        type: attachmentType,
        sort_order: attachment.sort_order,
        is_deleted: false
      })
      .select('id')
      .single();
    
    if (attachmentError) throw attachmentError;

    const attachmentId = attachmentData?.id;

    // Handle specific attachment types
    if (attachment.type === 'image') {
      await supabase.from('post_attachment_images').insert({
        post_attachment_id: attachmentId,
        image_url: attachment.data.image_url,
        file_name: attachment.data.file_name,
        file_size: attachment.data.file_size,
        mime_type: attachment.data.mime_type
      });
    } else if (attachment.type === 'protocol') {
      if (!attachmentId) {
        throw new Error('post_attachment_id is missing - cannot insert protocol');
      }
      if (!attachment.data || !attachment.data.id) {
        throw new Error('protocol_id is missing - cannot insert protocol attachment');
      }
      const { data: protocolData, error: protocolError } = await supabase
        .from('post_attachment_protocols')
        .insert({
          post_attachment_id: attachmentId,
          protocol_id: attachment.data.id,
          sort_order: 0
        })
        .select();
      if (protocolError) {
        console.error('Error inserting protocol attachment:', protocolError);
        throw protocolError;
      }
      if (!protocolData || protocolData.length === 0) {
        throw new Error('Failed to insert protocol attachment - no data returned');
      }
    } else if (attachment.type === 'biomarker') {
      if (!attachmentId) {
        throw new Error('post_attachment_id is missing - cannot insert biomarker');
      }
      
      if (!attachment.data || !attachment.data.id) {
        throw new Error('biomarker_id is missing - cannot insert biomarker');
      }
      // Lock in the specific result that was selected (so new results don't change this post)
      const resultId = attachment.data.result?.id
        ?? attachment.data.result_id
        ?? (Array.isArray(attachment.data.lab_results) && attachment.data.lab_results[0]?.id ? attachment.data.lab_results[0].id : null);
      const { data: biomarkerData, error: biomarkerError } = await supabase
        .from('post_attachment_biomarkers')
        .insert({
          post_attachment_id: attachmentId,
          biomarker_id: attachment.data.id,
          biomarker_result_id: resultId,
          sort_order: 0
        })
        .select();
      
      if (biomarkerError) {
        console.error('Error inserting biomarker:', biomarkerError);
        throw biomarkerError;
      }
      
      if (!biomarkerData || biomarkerData.length === 0) {
        throw new Error('Failed to insert biomarker - no data returned');
      }
      } else if (attachment.type === 'protocolReport') {
        if (!attachmentId) {
          throw new Error('post_attachment_id is missing - cannot insert protocol report');
        }
        
        if (!attachment.data || !attachment.data.id) {
          throw new Error('report_id is missing - cannot insert protocol report');
        }
        
        const { data: reportData, error: reportError } = await supabase
          .from('post_attachment_reports')
          .insert({
            post_attachment_id: attachmentId,
            report_id: attachment.data.id
          })
          .select();
        
        if (reportError) {
          console.error('Error inserting protocol report:', reportError);
          throw reportError;
        }
        
        if (!reportData || reportData.length === 0) {
          throw new Error('Failed to insert protocol report - no data returned');
        }
      }
  } catch (error) {
    console.error(`Error processing ${attachment.type} attachment:`, error);
    // Re-throw so callers can surface the error; avoid silent attachment failures
    throw error;
  }
};

export const prepareAttachmentsForSubmission = (attachments) => {
  let sortOrder = 0;
  const result = [];
  
  // Add images
  attachments.images.forEach(img => {
    result.push({ type: 'image', data: img, sort_order: sortOrder++ });
  });
  
  // Add protocol
  if (attachments.protocol) {
    result.push({ type: 'protocol', data: attachments.protocol, sort_order: sortOrder++ });
  }
  
  // Biohacks are now handled separately via createBiohackTags, not as attachments
  // Skip them here to avoid duplicate tag creation
  
  // Add biomarker
  if (attachments.biomarker) {
    result.push({ type: 'biomarker', data: attachments.biomarker, sort_order: sortOrder++ });
  }
  
  // Add protocol report
  if (attachments.protocolReport) {
    result.push({ type: 'protocolReport', data: attachments.protocolReport, sort_order: sortOrder++ });
  }
  
  return result;
};
